import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Input from '../components/ui/Input';
import ProgressBar from '../components/ProgressBar';
import Button from '../components/ui/Button';
import Footer from '../components/Footer';

/**
 * Login page for users coming from /start (with progress bar, no header)
 */
const StartLoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      setError(null);
      
      await login(formData.email, formData.password);
      
      // Check if there's a return destination from the location state
      const returnTo = location.state?.returnTo;
      const returnState = location.state?.returnState;
      
      if (returnTo && returnState) {
        navigate(returnTo, { state: returnState });
      } else {
        navigate('/start-confirmation');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Main content */}
      <main className="container mx-auto py-8 px-4">
        <div className="max-w-md w-full mx-auto">
          {/* Progress bar */}
          <ProgressBar currentStep={2} />
          
          {/* Title */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold">Log In</h2>
            <div className="w-16 h-1 bg-purple-500 mx-auto mt-2"></div>
            <p className="text-gray-400 mt-4">
              Log in to submit your availability
            </p>
          </div>
          
          {error && (
            <div className="mb-4 p-3 bg-red-900 bg-opacity-20 border border-red-700 rounded text-red-400">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <Input
              label="Email Address"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              required
            />
            
            <Input
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              required
            />
            
            <Button
              type="submit"
              variant="primary"
              fullWidth
              isLoading={isLoading}
              className="mt-4 bg-purple-600 hover:bg-purple-700"
            >
              Log In
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <p className="text-gray-400">
              Don't have an account?{' '}
              <button 
                type="button" 
                onClick={() => navigate('/start-register', { state: location.state })}
                className="text-purple-400 hover:text-purple-300 focus:outline-none"
              >
                Create Account
              </button>
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default StartLoginPage;